// src/components/QRScanPage.js
import React, { Component } from 'react';
import { API_ENDPOINTS, postRequest } from '../config/apiConfig';

const ICONS = {
  scan:     'M4 7V4h3M17 4h3v3M20 17v3h-3M7 20H4v-3M7 12h10M12 7v10',
  user:     'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  pill:     'M10.5 20H4a2 2 0 01-2-2V6a2 2 0 012-2h7M20 8V4a2 2 0 00-2-2h-2.5M14 12l-3 3M17 9l-3 3M21 15a6 6 0 11-8.485 8.485',
  flask:    'M9 3h6M9 3v6l-4.5 9A2 2 0 006.5 21h11a2 2 0 001.996-2.1L15 9V3',
  alert:    'M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  camera:   'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 17a4 4 0 100-8 4 4 0 000 8z',
  upload:   'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  keyboard: 'M20 5H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2z',
  info:     'M12 22a10 10 0 100-20 10 10 0 000 20zM12 8h.01M11 12h1v4h1',
};

function Ico({ name, size=18, color='currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{flexShrink:0}}>
      <path d={ICONS[name]||ICONS.info} />
    </svg>
  );
}

function btnStyle(bg) {
  return {
    width:'100%', height:56, borderRadius:14, border:'none',
    background:bg, color:bg==='#fff'?'#2d3452':'#fff',
    fontSize:14, fontWeight:700, cursor:'pointer',
    display:'flex', alignItems:'center', gap:14,
    padding:'0 20px', fontFamily:'inherit',
    boxShadow:bg==='#fff'?'none':'0 4px 14px rgba(0,0,0,.15)',
  };
}

function loadJsQR() {
  return new Promise(resolve => {
    if (window.jsQR) { resolve(); return; }
    // Try unpkg first, fallback to jsdelivr
    const urls = [
      'https://unpkg.com/jsqr@1.4.0/dist/jsQR.js',
      'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js',
    ];
    let idx = 0;
    const tryNext = () => {
      if (idx >= urls.length) { resolve(); return; }
      const s = document.createElement('script');
      s.src = urls[idx++];
      s.onload = resolve;
      s.onerror = tryNext;
      document.head.appendChild(s);
    };
    tryNext();
  });
}

function ResultCard({ icon, iconBg, iconColor, title, count, noun, badge, badgeBg, badgeColor, lines, emptyMsg, reports }) {
  const svgPath = icon==='pill'
    ? 'M10.5 20H4a2 2 0 01-2-2V6a2 2 0 012-2h7M20 8V4a2 2 0 00-2-2h-2.5M14 12l-3 3M17 9l-3 3M21 15a6 6 0 11-8.485 8.485'
    : 'M9 3h6M9 3v6l-4.5 9A2 2 0 006.5 21h11a2 2 0 001.996-2.1L15 9V3';

  const openFile = (filePath) => {
    if (!filePath) return;
    // Convert backslashes to forward slashes and remove leading ../
    const cleanPath = filePath.replace(/\\/g, '/').replace(/^\.\.\//, '');
    const host = window.location.hostname;
    const url  = cleanPath.startsWith('https') ? cleanPath : 'https://' + host + '/backend/' + cleanPath;
    window.open(url, '_blank');
  };

  return (
    <div style={{background:'#fff',borderRadius:20,border:'1px solid #E2E8F0',marginBottom:16,overflow:'hidden',boxShadow:'0 2px 12px rgba(0,0,0,.05)'}}>
      <div style={{padding:'16px 20px',borderBottom:'1px solid #E2E8F0',display:'flex',alignItems:'center',gap:12,background:iconBg+'33'}}>
        <div style={{width:38,height:38,borderRadius:10,background:iconBg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={svgPath}/></svg>
        </div>
        <div>
          <div style={{fontWeight:700,fontSize:15,color:'#0a0f1e'}}>{title}</div>
          <div style={{fontSize:12,color:'#6b748c'}}>{count>0?`${count} ${noun}${count!==1?'s':''}`:`No ${noun}s yet`}</div>
        </div>
      </div>
      <div style={{padding:'8px 20px 16px'}}>
        {lines.length===0 ? (
          <div style={{textAlign:'center',padding:'24px 0',color:'#94A3B8',fontSize:13}}>{emptyMsg}</div>
        ) : lines.map((line,i) => {
          const parts  = line.split(/\s*[–—·-]\s*/).map(p=>p.trim()).filter(Boolean);
          const name   = parts[0]||line;
          const rest   = parts.slice(1);
          // Match report by index or name for file path
          const report = reports && (reports[i] || reports.find(r =>
            (r.LabTestName||'').toLowerCase().includes(name.toLowerCase().split(' ')[0])
          ));
          const hasFile = report?.Path && report.Path !== null && report.Path !== '';
          return (
            <div key={i} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'12px 0',borderBottom:i<lines.length-1?'1px solid #F1F5F9':'none'}}>
              <div style={{width:36,height:36,borderRadius:10,background:iconBg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={svgPath}/></svg>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14,color:'#0a0f1e'}}>{name}</div>
                {rest.length>0 && <div style={{fontSize:12,color:'#6b748c',marginTop:3}}>{rest.join(' · ')}</div>}
                {/* Report status — only for lab tests (when reports prop passed) */}
                {reports && report?.Report_Status && (
                  <div style={{fontSize:11,color:'#6b748c',marginTop:3,textTransform:'capitalize'}}>
                    Status: <strong style={{color: report.Report_Status.toLowerCase()==='completed'?'#059669':'#6b748c'}}>{report.Report_Status}</strong>
                  </div>
                )}
              </div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,flexShrink:0}}>
                {reports && hasFile ? (
                  <button onClick={() => openFile(report.Path)}
                    style={{fontSize:11,fontWeight:700,padding:'5px 12px',borderRadius:20,border:'1.5px solid #7B5CF6',background:'#F0EDFE',color:'#7B5CF6',cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:4}}>
                    📄 View File
                  </button>
                ) : reports ? (
                  <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:'#F1F5F9',color:'#6b748c'}}>
                    {report?.Report_Status || 'No File'}
                  </span>
                ) : (
                  <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:badgeBg,color:badgeColor}}>{badge}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

class QRScanPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: 'home',
      manualId: '', uploadLoading: false, uploadError: null, previewSrc: null,
      cameraActive: false,
      inlineCamOn:  false,   // live camera shown inline on home page
      inlineCamErr: '',
      apptId: null, patientData: null, prescription: null, labResults: null, labReports: [], errorMsg: null,
    };
    this.fileRef   = React.createRef();
    this.cameraRef = React.createRef();
    this.videoRef = React.createRef();
    this.canvasRef = React.createRef();
    this.streamRef = null; this.scanLoop = null;
  }

  componentDidMount() {
    // Try to start inline camera automatically
    this.startInlineCamera();

    // Auto-load if URL has ?id=10 (from QR camera scan redirect)
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id') || params.get('ID');
    if (id) {
      this.fetchData(String(id));
    }
  }

  componentWillUnmount() { this.stopCamera(); this.stopInlineCamera(); }

  async decodeFromFile(file) {
    if (!file) return;
    await loadJsQR();
    this.setState({ mode:'upload', uploadLoading:true, uploadError:null });
    const reader = new FileReader();
    reader.onload = (e) => {
      this.setState({ previewSrc: e.target.result });
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else       { w = Math.round(w * MAX / h); h = MAX; }
        }
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h);
        let code = window.jsQR(d.data, d.width, d.height, { inversionAttempts:'dontInvert' });
        if (!code) code = window.jsQR(d.data, d.width, d.height, { inversionAttempts:'invertFirst' });
        this.setState({ uploadLoading: false });
        if (code) { this.handleQRData(code.data); }
        else { this.setState({ uploadError: 'Could not read QR. Make sure the QR fills most of the frame and is well-lit.' }); }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  async startCamera() {
    await loadJsQR();
    this.setState({ mode:'camera' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video:{ facingMode:{ideal:'environment'} } });
      this.streamRef = stream;
      this.setState({ cameraActive: true });
      setTimeout(() => {
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream;
          this.videoRef.current.play();
          this.videoRef.current.onloadedmetadata = () => this.startScanLoop();
        }
      }, 200);
    } catch(err) {
      this.setState({ mode:'home', uploadError:`Camera blocked on HTTP. Use "Upload QR Image" or "Take Photo" instead.` });
    }
  }

  stopCamera() {
    if (this.scanLoop) { cancelAnimationFrame(this.scanLoop); this.scanLoop = null; }
    if (this.streamRef) { this.streamRef.getTracks().forEach(t=>t.stop()); this.streamRef = null; }
    this.setState({ cameraActive: false });
  }

  async startInlineCamera() {
    await loadJsQR();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      this.streamRef = stream;
      this.setState({ inlineCamOn: true, inlineCamErr: '' });
      setTimeout(() => {
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream;
          this.videoRef.current.play();
          this.videoRef.current.onloadedmetadata = () => this.startScanLoop();
        }
      }, 300);
    } catch(err) {
      this.setState({ inlineCamOn: false, inlineCamErr: 'Camera not available (needs HTTPS on mobile network)' });
    }
  }

  stopInlineCamera() {
    if (this.scanLoop) { cancelAnimationFrame(this.scanLoop); this.scanLoop = null; }
    if (this.streamRef) { this.streamRef.getTracks().forEach(t=>t.stop()); this.streamRef = null; }
    this.setState({ inlineCamOn: false });
  }

  startScanLoop() {
    const tick = () => {
      const v = this.videoRef.current, c = this.canvasRef.current;
      if (!v||!c) return;
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext('2d').drawImage(v,0,0);
      const d = c.getContext('2d').getImageData(0,0,c.width,c.height);
      const code = window.jsQR(d.data,d.width,d.height,{inversionAttempts:'dontInvert'});
      if (code) { this.stopCamera(); this.handleQRData(code.data); return; }
      this.scanLoop = requestAnimationFrame(tick);
    };
    this.scanLoop = requestAnimationFrame(tick);
  }

  handleQRData(raw) {
    this.stopInlineCamera();
    console.log('QR raw data:', raw);
    let apptId = null;

    // 1. URL format: http://192.168.29.44:3000/scan?id=10
    try {
      const url = new URL(raw);
      apptId = url.searchParams.get('id') || url.searchParams.get('ID');
    } catch(_) {}

    // 2. JSON format: {"AppointmentID":"10"}
    if (!apptId) {
      try { const p = JSON.parse(raw); apptId = p.AppointmentID||p.appointmentId; } catch(_) {}
    }

    // 3. Pipe format: ID:10|PT:Ajay...
    if (!apptId) { const m = raw.match(/ID:(\d+)/); if (m) apptId = m[1]; }

    // 4. Plain number
    if (!apptId && /^\d+$/.test(raw.trim())) apptId = raw.trim();

    if (apptId) { this.fetchData(String(apptId)); }
    else { this.setState({ mode:'upload', uploadError:'Invalid QR — could not find Appointment ID.' }); }
  }

  async fetchData(apptId) {
    this.setState({ mode:'loading', apptId, patientData:null, prescription:null, labResults:null, errorMsg:null });
    try {
      const [rxRes, labRes] = await Promise.all([
        postRequest(API_ENDPOINTS.GET_PRESCRIPTION, { AppointmentID: apptId }),
        postRequest(API_ENDPOINTS.GET_LAB_RESULTS,  { AppointmentID: apptId }),
      ]);
      const [rxData, labData] = await Promise.all([rxRes.json(), labRes.json()]);
      const extract = d => {
        if (d.status && Array.isArray(d.data?.[0])) return d.data[0][0]||null;
        if (Array.isArray(d.data)) return d.data[0]||null;
        return null;
      };
      let patient = null;
      try {
        const pRes = await postRequest(API_ENDPOINTS.QR_VERIFY, { AppointmentID: apptId });
        const pData = await pRes.json();
        patient = extract(pData)||pData.data||null;
      } catch(_) {}
      const rx  = extract(rxData);
      const lab = extract(labData);

      // labReport is at top level of labData — data.labReport[0] array
      let labReports = [];
      if (Array.isArray(labData?.labReport?.[0]))  labReports = labData.labReport[0];
      else if (Array.isArray(labData?.labReport))  labReports = labData.labReport;
      console.log('labReports:', labReports);

      if (!patient) patient = rx||lab;
      this.setState({ mode:'result', patientData:patient, prescription:rx, labResults:lab, labReports });
    } catch(err) {
      this.setState({ mode:'error', errorMsg:err.message });
    }
  }

  reset() {
    this.stopCamera();
    if (this.fileRef.current) this.fileRef.current.value = '';
    // Remove ?id= from URL so refresh goes to home
    window.history.replaceState({}, '', '/scan');
    this.setState({ mode:'home', manualId:'', uploadError:null, previewSrc:null, apptId:null, patientData:null, prescription:null, labResults:null, errorMsg:null });
  }

  renderHome() {
    const { uploadError, inlineCamOn, inlineCamErr } = this.state;
    return (
      <div style={{maxWidth:480,margin:'0 auto'}}>

        {/* Live camera preview — shown when camera available */}
        <div style={{position:'relative',borderRadius:20,overflow:'hidden',background:'#000',marginBottom:16,aspectRatio:'4/3',minHeight:260}}>
          <video ref={this.videoRef} muted playsInline autoPlay
            style={{width:'100%',height:'100%',objectFit:'cover',display:inlineCamOn?'block':'none'}} />
          <canvas ref={this.canvasRef} style={{display:'none'}} />

          {/* Overlay corners */}
          {inlineCamOn && (
            <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none'}}>
              <div style={{width:200,height:200,position:'relative'}}>
                {[['0','0','borderTopLeftRadius'],['0','auto','borderTopRightRadius'],['auto','0','borderBottomLeftRadius'],['auto','auto','borderBottomRightRadius']].map(([t,r,_],i)=>(
                  <div key={i} style={{position:'absolute',top:i<2?0:'auto',bottom:i>=2?0:'auto',left:i%2===0?0:'auto',right:i%2===1?0:'auto',width:36,height:36,border:'3px solid #fff',borderRadius:4,
                    borderRight:i%2===1?'3px solid #fff':'none',borderLeft:i%2===0?'3px solid #fff':'none',
                    borderBottom:i>=2?'3px solid #fff':'none',borderTop:i<2?'3px solid #fff':'none'}} />
                ))}
                <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',color:'rgba(255,255,255,.6)',fontSize:11,textAlign:'center',whiteSpace:'nowrap'}}>
                  Point at QR code
                </div>
              </div>
            </div>
          )}

          {/* No camera placeholder */}
          {!inlineCamOn && (
            <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'#fff',gap:10}}>
              <div style={{width:64,height:64,borderRadius:20,background:'rgba(255,255,255,.1)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Ico name="camera" size={28} color="rgba(255,255,255,.7)" />
              </div>
              <div style={{fontSize:13,color:'rgba(255,255,255,.7)',textAlign:'center',padding:'0 20px'}}>
                {inlineCamErr || 'Starting camera…'}
              </div>
              {inlineCamErr && (
                <button onClick={()=>this.startInlineCamera()}
                  style={{padding:'8px 18px',borderRadius:20,border:'1.5px solid rgba(255,255,255,.4)',background:'transparent',color:'#fff',fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>
                  Try Again
                </button>
              )}
            </div>
          )}
        </div>

        {uploadError && (
          <div style={{background:'#FFF0EB',border:'1px solid #fcd9c4',borderRadius:14,padding:'12px 16px',marginBottom:12,display:'flex',gap:10,alignItems:'flex-start',fontSize:13,color:'#FF6B35'}}>
            <Ico name="alert" size={15} color="#FF6B35" /> {uploadError}
          </div>
        )}

        {/* Hidden inputs */}
        <input ref={this.fileRef} type="file" accept="image/*" style={{display:'none'}}
          onChange={e => { if(e.target.files[0]) this.decodeFromFile(e.target.files[0]); }} />
        <input ref={this.cameraRef} type="file" accept="image/*" capture="environment" style={{display:'none'}}
          onChange={e => { if(e.target.files[0]) this.decodeFromFile(e.target.files[0]); }} />

        {/* Action buttons */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <button onClick={()=>{ this.fileRef.current.value=''; this.fileRef.current.click(); }} style={{...btnStyle('#0A6EFF'),justifyContent:'center',flexDirection:'column',gap:6,padding:'14px 10px',textAlign:'center'}}>
            <Ico name="upload" size={20} color="#fff" />
            <div style={{fontSize:13,fontWeight:700}}>Upload Image</div>
          </button>
          <button onClick={()=>{ this.cameraRef.current.value=''; this.cameraRef.current.click(); }} style={{...btnStyle('#059669'),justifyContent:'center',flexDirection:'column',gap:6,padding:'14px 10px',textAlign:'center'}}>
            <Ico name="camera" size={20} color="#fff" />
            <div style={{fontSize:13,fontWeight:700}}>Take Photo</div>
          </button>
        </div>
        <button onClick={()=>this.setState({mode:'manual'})} style={{...btnStyle('#fff'),border:'1.5px solid #E2E8F0',color:'#2d3452',width:'100%',justifyContent:'center',gap:8}}>
          <Ico name="keyboard" size={18} color="#2d3452" />
          <span style={{fontWeight:700,color:'#0a0f1e'}}>Enter Appointment ID manually</span>
        </button>
      </div>
    );
  }

  renderUpload() {
    const { uploadLoading, uploadError, previewSrc } = this.state;
    return (
      <div style={{maxWidth:440,margin:'0 auto',textAlign:'center'}}>
        {previewSrc && <img src={previewSrc} alt="QR" style={{width:'100%',maxWidth:260,borderRadius:16,marginBottom:16,border:'2px solid #E2E8F0'}} />}
        {uploadLoading && (
          <div style={{padding:'32px 0'}}>
            <div style={{width:48,height:48,border:'4px solid #E2E8F0',borderTop:'4px solid #0A6EFF',borderRadius:'50%',animation:'qrSpin 1s linear infinite',margin:'0 auto 14px'}} />
            <div style={{fontWeight:600,color:'#2d3452'}}>Reading QR code…</div>
          </div>
        )}
        {uploadError && (
          <div style={{background:'#FFF0EB',border:'1px solid #fcd9c4',borderRadius:14,padding:'14px 16px',marginBottom:16,display:'flex',gap:10,alignItems:'flex-start',textAlign:'left'}}>
            <Ico name="alert" size={16} color="#FF6B35" />
            <div style={{fontSize:13,color:'#FF6B35'}}>{uploadError}</div>
          </div>
        )}
        {!uploadLoading && <button onClick={()=>this.reset()} style={{...btnStyle('#6b748c'),justifyContent:'center',marginTop:8}}>← Try Again</button>}
      </div>
    );
  }

  renderCamera() {
    const { cameraActive } = this.state;
    return (
      <div style={{maxWidth:440,margin:'0 auto'}}>
        <div style={{background:'#0a0f1e',borderRadius:20,overflow:'hidden',aspectRatio:'1',position:'relative',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <video ref={this.videoRef} muted playsInline style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />
          <canvas ref={this.canvasRef} style={{display:'none'}} />
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{width:'60%',height:'60%',position:'relative'}}>
              {[[0,0],[0,1],[1,0],[1,1]].map(([r,c],i)=>(
                <div key={i} style={{position:'absolute',width:24,height:24,
                  top:r?'auto':0,bottom:r?0:'auto',left:c?'auto':0,right:c?0:'auto',
                  borderTop:!r?'3px solid #00C896':undefined,borderBottom:r?'3px solid #00C896':undefined,
                  borderLeft:!c?'3px solid #00C896':undefined,borderRight:c?'3px solid #00C896':undefined}} />
              ))}
              <div style={{position:'absolute',left:0,right:0,height:2,background:'linear-gradient(90deg,transparent,#00C896,transparent)',animation:'qrScanLine 2s ease-in-out infinite',top:'50%'}} />
            </div>
          </div>
          {!cameraActive && <div style={{position:'absolute',color:'rgba(255,255,255,.5)',fontSize:13}}>Starting camera…</div>}
        </div>
        <button onClick={()=>{this.stopCamera();this.reset();}} style={{...btnStyle('#FF4757'),justifyContent:'center',width:'100%'}}>
          Stop & Go Back
        </button>
      </div>
    );
  }

  renderManual() {
    const { manualId } = this.state;
    return (
      <div style={{maxWidth:440,margin:'0 auto'}}>
        <div style={{textAlign:'center',marginBottom:24}}>
          <div style={{width:60,height:60,borderRadius:16,background:'#E8F1FF',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
            <Ico name="keyboard" size={26} color="#0A6EFF" />
          </div>
          <h3 style={{fontSize:18,fontWeight:800,color:'#0a0f1e',marginBottom:6}}>Enter Appointment ID</h3>
          <p style={{color:'#6b748c',fontSize:13}}>Type the appointment number from the QR</p>
        </div>
        <input type="number" placeholder="e.g. 10" value={manualId} autoFocus
          onChange={e=>this.setState({manualId:e.target.value})}
          onKeyDown={e=>e.key==='Enter'&&manualId&&this.fetchData(manualId)}
          style={{width:'100%',height:58,borderRadius:14,border:'1.5px solid #E2E8F0',padding:'0 18px',fontSize:22,fontWeight:800,fontFamily:'inherit',outline:'none',textAlign:'center',boxSizing:'border-box',marginBottom:12}}
          onFocus={e=>e.target.style.borderColor='#0A6EFF'} onBlur={e=>e.target.style.borderColor='#E2E8F0'} />
        <button onClick={()=>manualId&&this.fetchData(manualId)} disabled={!manualId}
          style={{...btnStyle('#0A6EFF'),justifyContent:'center',opacity:manualId?1:.5,marginBottom:10}}>
          Search Appointment →
        </button>
        <button onClick={()=>this.reset()} style={{...btnStyle('#fff'),justifyContent:'center',border:'1.5px solid #E2E8F0',color:'#2d3452'}}>
          ← Back
        </button>
      </div>
    );
  }

  renderLoading() {
    return (
      <div style={{textAlign:'center',padding:'60px 20px'}}>
        <div style={{width:56,height:56,border:'4px solid #E2E8F0',borderTop:'4px solid #0A6EFF',borderRadius:'50%',animation:'qrSpin 1s linear infinite',margin:'0 auto 20px'}} />
        <div style={{fontWeight:700,fontSize:16,color:'#0a0f1e',marginBottom:6}}>Loading appointment #{this.state.apptId}…</div>
        <div style={{color:'#6b748c',fontSize:13}}>Fetching patient info, medicines & lab tests</div>
      </div>
    );
  }

  renderError() {
    return (
      <div style={{maxWidth:440,margin:'0 auto',textAlign:'center'}}>
        <div style={{background:'#FFF0EB',border:'1px solid #fcd9c4',borderRadius:20,padding:'32px 24px',marginBottom:16}}>
          <Ico name="alert" size={40} color="#FF6B35" />
          <div style={{fontWeight:700,fontSize:17,color:'#FF6B35',marginTop:12,marginBottom:8}}>Failed to load</div>
          <div style={{color:'#6b748c',fontSize:13}}>{this.state.errorMsg}</div>
        </div>
        <button onClick={()=>this.reset()} style={{...btnStyle('#0A6EFF'),justifyContent:'center',width:'100%'}}>← Try Again</button>
      </div>
    );
  }

  renderResult() {
    const { apptId, patientData:p, prescription, labResults, labReports } = this.state;
    const n = s => (s||'').trim();
    const patientName = p ? `${n(p.PatientFirstName||p.patfname)} ${n(p.PatientLastName||p.patlname)}`.trim()||`Patient #${apptId}` : `Appointment #${apptId}`;
    const doctorName  = p ? `Dr. ${n(p.DoctorFirstName)} ${n(p.DoctorLastName)}`.trim() : '—';
    const dept  = p?.DepartmentName||'—';
    const spec  = p?.Specilization||'';
    const date  = p?.Appointmentdate||p?.AppointmentDate||'';
    const fmtDate = date ? new Date(date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
    const time   = p?.Time||'—';
    const gender = p?.PatientGender||'—';
    const blood  = p?.PatientBloodGroup||'—';
    const dob    = p?.PatientDOB||'';
    const age    = dob ? Math.floor((Date.now()-new Date(dob).getTime())/(1000*60*60*24*365.25)) : '—';
    const status = p?.PaymentStatus||'—';
    const isPaid = ['paid','completed'].includes(status.toLowerCase());
    const reason = p?.Reason||'—';
    const fees   = p?.Payment!=null?`₹${p.Payment}`  :'—';
    const initials = patientName.split(' ').map(w=>w[0]||'').slice(0,2).join('').toUpperCase()||'PT';
    const medText  = prescription?.Medicines_details||prescription?.Medicines||'';
    const splitLines = t => t ? t.split(/\r\n|\r|\n|\\n|\\r/).map(l=>l.replace(/^[0-9]+[.)\s]*/,'').trim()).filter(Boolean) : [];
    const medLines = splitLines(medText);
    const labText  = labResults?.Lab_test_details||labResults?.LabTests||'';
    const labLines = splitLines(labText);
    // Also get lab reports for view file button
    // labReports come from state (parsed at fetch time from top-level response)
    return (
      <div style={{maxWidth:560,margin:'0 auto'}}>
        <div style={{background:'linear-gradient(135deg,#0A6EFF,#0052CC)',borderRadius:20,padding:24,marginBottom:16,color:'#fff'}}>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:20}}>
            <div style={{width:56,height:56,borderRadius:16,background:'rgba(255,255,255,.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,fontWeight:800,flexShrink:0}}>{initials}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:19}}>{patientName}</div>
              <div style={{fontSize:13,opacity:.8,marginTop:2}}>{age} yrs · {gender} · {blood}</div>
            </div>
            <span style={{padding:'5px 12px',borderRadius:20,background:isPaid?'rgba(0,200,150,.25)'  :'rgba(255,107,53,.25)',color:isPaid?'#a7f3d0':'#fcd9c4',fontSize:12,fontWeight:700,flexShrink:0}}>{status}</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 20px',fontSize:13}}>
            {[{l:'Doctor',v:doctorName},{l:'Department',v:dept},{l:'Spec.',v:spec||'—'},{l:'Appt #',v:apptId},{l:'Date',v:fmtDate},{l:'Time',v:time},{l:'Fees',v:fees},{l:'Reason',v:reason}].map((r,i)=>(
              <div key={i}><div style={{opacity:.6,fontSize:11,textTransform:'uppercase',letterSpacing:.5,marginBottom:2}}>{r.l}</div><div style={{fontWeight:600}}>{r.v}</div></div>
            ))}
          </div>
        </div>
        {/* Edit button for doctors */}
        {(() => {
          try {
            const u = JSON.parse(localStorage.getItem('dg_user') || '{}');
            if (u.role === 'doctor') {
              const scanUrl = window.location.origin + '/doctorDashboard';
              return (
                <div style={{marginBottom:16,textAlign:'right'}}>
                  <button
                    onClick={() => {
                      // Store apptId so doctor dashboard can pick it up
                      localStorage.setItem('dg_prescribe_apptId', apptId);
                      window.location.href = scanUrl;
                    }}
                    style={{padding:'10px 22px',borderRadius:12,border:'none',background:'#7B5CF6',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:8}}>
                    ✏️ Write Prescription
                  </button>
                </div>
              );
            }
          } catch(_) {}
          return null;
        })()}

        {/* QR Code */}
{/* QR Code section removed - QRCodeBlock not available in this page */}

        <ResultCard icon="pill" iconBg="#FFF4EC" iconColor="#FF6B35" title="💊 Prescribed Medicines" count={medLines.length} noun="medicine" badge="ACTIVE" badgeBg="#E6FAF5" badgeColor="#059669" lines={medLines} emptyMsg="No medicines prescribed yet" />
        <ResultCard icon="flask" iconBg="#F0EDFE" iconColor="#7B5CF6" title="🔬 Lab Test Orders" count={labLines.length} noun="test" badge="ORDERED" badgeBg="#E8F1FF" badgeColor="#0A6EFF" lines={labLines} emptyMsg="No lab tests ordered yet" reports={labReports} />
        <button onClick={()=>this.reset()} style={{...btnStyle('#0A6EFF'),justifyContent:'center',width:'100%',marginBottom:20}}>
          <Ico name="scan" size={18} color="#fff" /> Scan Another QR
        </button>
      </div>
    );
  }

  render() {
    const { mode } = this.state;
    return (
      <div style={{minHeight:'100vh',background:'#F7F9FC',fontFamily:"'Plus Jakarta Sans','Inter',sans-serif"}}>
        <style>{`@keyframes qrScanLine{0%{top:10%}50%{top:85%}100%{top:10%}}@keyframes qrSpin{to{transform:rotate(360deg)}}`}</style>
        <div style={{background:'#fff',borderBottom:'1px solid #E2E8F0',padding:'14px 20px',display:'flex',alignItems:'center',gap:12,position:'sticky',top:0,zIndex:100,boxShadow:'0 1px 8px rgba(0,0,0,.06)'}}>
          <div style={{width:38,height:38,borderRadius:12,background:'linear-gradient(135deg,#0A6EFF,#0052CC)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <Ico name="scan" size={18} color="#fff" />
          </div>
          <div style={{flex:1}}>
            <div style={{fontWeight:800,fontSize:16,color:'#0a0f1e'}}>DG Doctor — QR Scanner</div>
            <div style={{fontSize:11,color:'#6b748c'}}>Scan patient QR to view full details</div>
          </div>
          {['result','loading','error'].includes(mode) && (
            <button onClick={()=>this.reset()} style={{border:'1.5px solid #E2E8F0',background:'#fff',borderRadius:10,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer',color:'#2d3452',fontFamily:'inherit'}}>← New Scan</button>
          )}
        </div>
        <div style={{padding:'24px 16px',maxWidth:620,margin:'0 auto'}}>
          {mode==='home'    && this.renderHome()}
          {mode==='upload'  && this.renderUpload()}
          {mode==='camera'  && this.renderCamera()}
          {mode==='manual'  && this.renderManual()}
          {mode==='loading' && this.renderLoading()}
          {mode==='error'   && this.renderError()}
          {mode==='result'  && this.renderResult()}
        </div>
      </div>
    );
  }
}

export default QRScanPage;